<script setup>
import {ref} from 'vue';
import route from 'ziggy';
import {useForm} from '@inertiajs/vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import PublicLayout from '@/Layouts/PublicLayout.vue';
import DaisyLink from '@/Components/Typography/DaisyLink.vue';
import DaisyAnchor from '@/Components/Typography/DaisyAnchor.vue';

const props = defineProps({
    user: {
        type: Object,
        default: null
    }
})

const form = useForm({
    name: ref(props.user?.name ?? ''),
    email: ref(props.user?.email ?? ''),
    password: '',
    password_confirmation: '',
    terms: false,
});

// const updateEmail = (value) => {
//     form.email = value;
// };

const submit = () => {
    form.post(route('register'), {
        onFinish: () => form.reset('password', 'password_confirmation'),
    });
};
</script>

<template>
    <PublicLayout :title="$t('menu.register')">

        <AuthenticationCard>
            <template #logo>
                <AuthenticationCardLogo/>
            </template>

            <form @submit.prevent="submit">
                <div>
                    <InputLabel for="name" :value="$t('Name')"/>
                    <TextInput
                            id="name"
                            v-model="form.name"
                            :value="user ? user.name : form.name"
                            type="text"
                            class="mt-1 block w-full"
                            required
                            autofocus
                            autocomplete="name"
                    />
                    <InputError class="mt-2" :message="form.errors.name"/>
                </div>

                <div class="mt-4">
                    <InputLabel for="email" :value="$t('Email')"/>
                    <TextInput
                            id="email"
                            v-model="form.email"
                            :value="user ? user.email : form.email"
                            type="email"
                            class="mt-1 block w-full"
                            required
                            :readonly="user !== null"
                            autocomplete="username"
                    />
                    <InputError class="mt-2" :message="form.errors.email"/>
                </div>

                <div class="mt-4">
                    <InputLabel for="password" :value="$t('Password')"/>
                    <TextInput
                            id="password"
                            v-model="form.password"
                            type="password"
                            class="mt-1 block"
                            required
                            autocomplete="new-password"
                    />
                    <InputError class="mt-2" :message="form.errors.password"/>
                </div>

                <div class="mt-4">
                    <InputLabel for="password_confirmation" :value="$t('Confirm Password')"/>
                    <TextInput
                            id="password_confirmation"
                            v-model="form.password_confirmation"
                            type="password"
                            class="mt-1 block"
                            required
                            autocomplete="new-password"
                    />
                    <InputError class="mt-2" :message="form.errors.password_confirmation"/>
                </div>

                <div v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature" class="mt-4">
                    <InputLabel for="terms">
                        <div class="flex items-center">
                            <Checkbox id="terms" v-model:checked="form.terms" name="terms" required/>

                            <div class="ms-2">
                                {{ $t('I agree to the') }}
                                <DaisyAnchor target="_blank" :href="route('terms.show')">{{
                                    $t('Terms of Service')
                                    }}
                                </DaisyAnchor>
                                {{ $t('tos_end') }}
                                <DaisyAnchor target="_blank" :href="route('policy.show')">{{
                                    $t('Privacy Policy')
                                    }}
                                </DaisyAnchor>
                            </div>
                        </div>
                        <InputError class="mt-2" :message="form.errors.terms"/>
                    </InputLabel>
                </div>

                <div class="flex items-center justify-end mt-4">
                    <DaisyLink :href="route('login')">{{ $t('Already registered?') }}</DaisyLink>

                    <PrimaryButton class="ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ $t('Register') }}
                    </PrimaryButton>
                </div>
            </form>
        </AuthenticationCard>
    </PublicLayout>
</template>
